/* eslint-disable max-lines */
import { openDB, type IDBPDatabase, type IDBPObjectStore, type StoreValue } from 'idb'

import type {
  MyDB,
  ObjectStoreName,
  ObjectStoreNameWithCRUD,
  Method,
  SendState,
  IndexedDBData
} from '@/types/accessor/indexedDB'
import {
  OBJECT_STORE_NAME_WITH_CRUD,
  OBJECT_STORE_NAME_WITHOUT_CRUD
} from '@/constants/accessor/indexedDB/objectStoreName'
import MiiaFwConfig from '@/configs/config'
import type { ResultSummary } from '@/types/accessor/api/emergencyInspectionResult/emergencyInspectionResult'

export type IndexName = 'method' | 'send_state' | 'update_date_time'

// top-level awaitが使用できないため関数で代入する
let db: IDBPDatabase<MyDB>
const createDB = async (): Promise<void> => {
  if (db !== undefined) return
  // もしobjectStoreの構造を変更した場合、src/define/config.tsのMiiaFwConfig.config.disasterEmergency.indexedDBVersion.apiDBの値を1増加させる
  db = await openDB<MyDB>('apiDB', MiiaFwConfig.config.disasterEmergency.indexedDBVersion.apiDB, {
    upgrade: async db => {
      try {
        // createObjectStoreする際に同じobjectStoreがある場合エラーとなってしまうため、一度全部削除する
        const objectStoreNames = [...OBJECT_STORE_NAME_WITH_CRUD, ...OBJECT_STORE_NAME_WITHOUT_CRUD]
        objectStoreNames.forEach(objectStoreName => {
          if (db.objectStoreNames.contains(objectStoreName)) {
            db.deleteObjectStore(objectStoreName)
          }
        })
        // CRUD操作がないStore
        db.createObjectStore('building')
        db.createObjectStore('disaster')
        db.createObjectStore('government_setting')
        db.createObjectStore('admin_districts')

        // CRUD操作が必要なStoreに共通Indexを付与する
        const createObjectStoreWithIndex = (
          storeName: ObjectStoreNameWithCRUD,
          autoIncrement: boolean = false
        ): IDBPObjectStore<
          MyDB,
          ArrayLike<ObjectStoreName>,
          ObjectStoreNameWithCRUD,
          'versionchange'
        > => {
          const store = db.createObjectStore(storeName, { autoIncrement })
          store.createIndex('method', 'method')
          store.createIndex('send_state', 'send_state')
          store.createIndex('update_date_time', 'update_date_time')
          return store
        }

        // CRUD操作が必要なStore
        createObjectStoreWithIndex('inspection_result')
      } catch (err) {
        if (err instanceof Error) {
          throw err
        }
      }
    }
  })
}

/**
 * 指定したindexedDBのobjectStoreにデータを挿入する
 * @param storeName ObjectStoreの名前
 * @param setData 挿入したいデータ
 * @param method データの操作内容
 * @param sendState データのステータス
 * @param key プライマリキー
 */
export const set = async (
  storeName: ObjectStoreName,
  setData: object,
  method: Method,
  sendState: SendState,
  key: string = ''
): Promise<void> => {
  await createDB()
  try {
    const tx = db.transaction(storeName, 'readwrite')
    const store = tx.objectStore(storeName)
    // CRUD操作が存在するStoreの場合
    if (OBJECT_STORE_NAME_WITH_CRUD.some(v => v === storeName)) {
      // 既に登録がある場合、methodとsend_stateを切り替える
      const unixTime = Math.round(new Date().getTime() / 1000)
      const settingData = {
        data: setData,
        method,
        send_state: sendState,
        update_date_time: unixTime
      }
      // keyがautoIncrementの場合
      if (key === '') {
        await store.put(settingData)
        await tx.done
        return
      }
      await store.put(settingData, key)
      await tx.done
      return
    }
    // keyがautoIncrementの場合
    if (key === '') {
      await store.put(setData)
      await tx.done
      return
    }
    await store.put(setData, key)
    await tx.done
  } catch (err) {
    throw err
  }
}

export const deleteByKey = async (storeName: ObjectStoreName, key: string): Promise<void> => {
  const tx = db.transaction(storeName, 'readwrite')
  const store = tx.objectStore(storeName)
  await store.delete(key)
  await tx.done
}

/**
 * 指定したobjectStoreからデータを一つ取得する
 * @param storeName ObjectStoreの名前
 * @param key プライマリキー
 * @returns data
 */
export const get = async <T>(storeName: ObjectStoreName, key: string): Promise<T | undefined> => {
  await createDB()
  try {
    const tx = db.transaction(storeName, 'readonly')
    const store = tx.objectStore(storeName)
    // keyが存在するかどうかチェック
    const allKeys = await store.getAllKeys()
    if (!allKeys.includes(key)) {
      await tx.done
      return undefined
    }
    const ret = await store.get(key)
    await tx.done
    return ret as T
  } catch (err) {
    throw err
  }
}

/**
 * 指定したobjectStoreからデータをすべて取得する
 * @param storeName objectStoreの名前
 * @returns data
 */
export const getAll = async <T>(storeName: ObjectStoreName): Promise<T[] | undefined> => {
  await createDB()
  try {
    const tx = db.transaction(storeName, 'readonly')
    const store = tx.objectStore(storeName)
    const ret = await store.getAll()
    await tx.done
    return ret as T[]
  } catch (err) {
    throw err
  }
}

/**
 * 指定したobjectStoreからデータをすべてのデータを削除する
 * @param storeName objectStoreの名前
 * @returns なし
 */
export const clear = async (storeName: ObjectStoreName): Promise<void> => {
  await createDB()
  try {
    const tx = db.transaction(storeName, 'readwrite')
    const store = tx.objectStore(storeName)
    await store.clear()
    await tx.done
  } catch (err) {
    throw err
  }
}

// errorハンドリング用関数
// TODO: エラーハンドリングは仮決め。別タスクで作りこむ
const errorHandler = async (occurredName: string, err: Error): Promise<never> => {
  return await new Promise((resolve, reject) => {
    reject({
      message: {
        message: `indexedDB:${occurredName}アクセス時にエラー発生\n${err.message}`,
        code: '600'
      }
    })
  })
}

/**
 * 指定したデータのsendStateを更新する
 * @param storeName objectStoreの名前
 * @param sendState 更新するsendStateの値
 * @param key 更新対象のレコード
 * @returns data
 */
export const updateSendState = async (
  storeName: ObjectStoreName,
  sendState: SendState,
  key: string
): Promise<void> => {
  await createDB()
  const res = (await get(storeName, key)) as IndexedDBData
  if (res === undefined) return
  const settingData = {
    data: res.data,
    method: res.method,
    send_state: sendState,
    update_date_time: Math.round(new Date().getTime() / 1000)
  }
  try {
    const tx = db.transaction(storeName, 'readwrite')
    const store = tx.objectStore(storeName)
    await store.put(settingData, key)
    await tx.done
  } catch (err) {
    await errorHandler(storeName, err as Error)
  }
}

/**
 * 指定したobjectStoreから指定したindexのデータをすべて取得する
 * @param storeName objectStoreの名前
 * @returns data
 */
export const getAllFromIndex = async (
  storeName: ObjectStoreNameWithCRUD,
  indexName: IndexName,
  index: string
): Promise<StoreValue<string, string>[]> => {
  await createDB()
  try {
    const tx = db.transaction(storeName, 'readonly')
    const store = tx.objectStore(storeName).index(indexName)
    const data = await store.getAll(index)
    await tx.done
    return data
  } catch (err) {
    throw err
  }
}

/**
 * 指定したobjectStoreからキー情報をすべて取得する
 * @param storeName objectStoreの名前
 * @returns data
 */
export const getAllKeys = async (storeName: ObjectStoreName): Promise<string[]> => {
  await createDB()
  try {
    const tx = db.transaction(storeName, 'readonly')
    const store = tx.objectStore(storeName)
    const data = await store.getAllKeys()
    await tx.done
    return data
  } catch (err) {
    throw err
  }
}

/**
 * 有効期限切れのデータを一括削除する
 * @param thresholdDate 削除の閾値日時
 * @returns なし
 */
export const bulkDeleteExpiredItemsFromIDB = async (
  storeName: ObjectStoreName,
  thresholdDate: Date = new Date()
): Promise<void> => {
  // 現状、調査結果以外の場合は想定していない
  if (storeName !== 'inspection_result') return

  await createDB()
  let cursor = await db.transaction(storeName, 'readwrite').objectStore(storeName).openCursor()
  while (await cursor) {
    if (cursor == null) {
      return
    }

    const data = cursor.value.data as ResultSummary
    const insertDate = new Date(data.insertDate)
    // 調査日時 < 現在時間だったら削除する
    if (isPastDate(thresholdDate, insertDate)) {
      await cursor.delete().catch(async err => {
        throw err
      })
    }
    cursor = await cursor.continue()
  }
}

/**
 * 日付を比較する
 * @param targetDate
 * @returns boolean 実行日より前であればtrue
 */
function isPastDate(thresholdDate: Date, targetDate: Date): boolean {
  // 現在の日時を取得（時分秒をゼロにセット）
  const today = thresholdDate
  today.setHours(0, 0, 0, 0)

  // 対象の日時を取得（時分秒をゼロにセット）
  const compareDate = targetDate
  compareDate.setHours(0, 0, 0, 0)

  // 比較（対象の日時が今日よりも前ならtrue）
  return compareDate < today
}
